import { PatchType } from '../../../types';

/**
 * Shared constants for operation types organized by patch type.
 * This avoids duplication across components and provides a single source of truth.
 */
export const OPERATION_TYPES_BY_PATCH_TYPE: Record<PatchType, string[]> = {
  [PatchType.PRODUCT_PRICE]: [
    'SetVariantPriceMenuPatchOperation',
    'SetModifierWeightPriceMenuPatchOperation',
    'SetModifierPriceRangesMenuPatchOperation',
  ],
  [PatchType.BUNDLE_PRICE]: [
    'SetBundlePriceMenuPatchOperation',
  ],
  [PatchType.PRODUCT_EXCLUSION]: [
    'RemoveProductMenuPatchOperation',
    'RemoveVariantMenuPatchOperation',
    'RemoveModifierFromSlotPatchOperation',
    'RemoveModifierMenuPatchOperation',
    'SetModifierWeightIsExcludedFromVariantMenuPatchOperation',
    'RemoveWeightFromSlotModifierMenuPatchOperation',
  ],
  [PatchType.BUNDLE_EXCLUSION]: [
    'RemoveBundleMenuPatchOperation',
  ]
};

/**
 * Operation types that are only offered while the named pricing flag is on. A named price is a
 * store-level price shared across every product/variant that resolves to it, so it belongs with the
 * product price operations.
 */
export const NAMED_PRICING_OPERATION_TYPES: string[] = [
  'SetNamedPriceAmountPatchOperation',
];

/**
 * Ranged topping/extra-cheese pricing. Superseded by named pricing: hidden per environment once its
 * data is migrated, then removed outright in phase 3 along with this constant.
 */
export const RANGED_PRICING_OPERATION_TYPES: string[] = [
  'SetModifierPriceRangesMenuPatchOperation',
];

export interface OperationTypeFlags {
  namedPricingEnabled: boolean;
  rangedPricingEnabled: boolean;
  /**
   * Keep flagged-off operation types selectable so pricing a store already has can still be cleared.
   * Set for the drawer's remove mode: hiding ranged pricing must stop new ranges being created, not
   * strand the ranges already out there.
   */
  allowRetiredForRemoval?: boolean;
}

/**
 * Helper function to get available operation types for a given patch type
 */
export const getOperationTypesForPatchType = (patchType: PatchType): string[] => {
  return OPERATION_TYPES_BY_PATCH_TYPE[patchType] || [];
};

/**
 * The operation types a user may actually pick, after feature flags are applied.
 *
 * Callers pass flag state in rather than reading it here so this stays a pure function - see
 * OperationDrawer for the Optimizely wiring.
 */
export const getVisibleOperationTypes = (
  patchType: PatchType,
  { namedPricingEnabled, rangedPricingEnabled, allowRetiredForRemoval = false }: OperationTypeFlags
): string[] => {
  const base = getOperationTypesForPatchType(patchType);

  const visible =
    rangedPricingEnabled || allowRetiredForRemoval
      ? base
      : base.filter((type) => !RANGED_PRICING_OPERATION_TYPES.includes(type));

  if (!namedPricingEnabled || patchType !== PatchType.PRODUCT_PRICE) {
    return visible;
  }

  return [...visible, ...NAMED_PRICING_OPERATION_TYPES.filter((type) => !visible.includes(type))];
};

/**
 * Helper function to format operation type names for display
 */
export const formatOperationTypeName = (operationType: string): string => {
  return operationType
    .replace('MenuPatchOperation', '')
    .replace('PatchOperation', '')
    .replace(/([A-Z])/g, ' $1')
    .trim();
};